import React from "react";
import { Link } from "react-router-dom";
import { Anchor, Award, Users } from "lucide-react";

const STAFF = [
  { name: "Ashley Chandler", role: "Staff Writer", avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&q=85&w=240&h=240&fit=crop" },
  { name: "Nick Blank", role: "Sports Editor", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=240&h=240&fit=crop" },
  { name: "Amy Mulligan", role: "Staff Writer", avatar: "https://images.unsplash.com/photo-1609436132311-e4b0c9370469?crop=entropy&cs=srgb&fm=jpg&q=85&w=240&h=240&fit=crop" },
  { name: "Dave Scott", role: "Columnist", avatar: "https://images.unsplash.com/photo-1543949806-2c9935e6aa78?crop=entropy&cs=srgb&fm=jpg&q=85&w=240&h=240&fit=crop" },
];

export default function About() {
  return (
    <div className="paper-texture min-h-screen">
      <div className="relative overflow-hidden border-b border-[var(--paper-border)]">
        <img src="https://images.unsplash.com/photo-1595432849291-3036ea5e5475?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Amelia Island pier" className="h-72 w-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(12,35,64,0.78)" }} />
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto w-full max-w-7xl px-4">
            <p className="eyebrow flex items-center gap-2 text-[var(--gold)]"><Anchor size={14} /> Established 1854</p>
            <h1 className="font-display text-4xl font-extrabold text-white sm:text-5xl">Florida's Oldest Weekly Newspaper</h1>
            <p className="font-serif-body mt-3 max-w-2xl text-lg text-white/90">
              For more than 170 years, the News-Leader has chronicled life on Amelia Island and across Nassau County.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-14">
        <h2 className="section-rule font-display pt-3 text-2xl font-bold text-[var(--navy)]">Our story</h2>
        <div className="font-serif-body mt-5 space-y-5 text-lg leading-[1.85] text-slate-800">
          <p>
            Founded in 1854 in the historic port town of Fernandina Beach, the News-Leader is one of the oldest newspapers in Florida. Through hurricanes, booms, busts and the slow turning of the tides, we've been the paper of record for Amelia Island, Fernandina Beach, Yulee and all of Nassau County.
          </p>
          <p>
            Our mission is simple: cover this community the way it deserves to be covered — with care, accuracy and a deep love for the place we call home. From the City Commission to Friday-night football, from the shrimp docks to the historic district, if it matters to Nassau County, you'll read it here first.
          </p>
          <p>
            Today we're building the next chapter: a modern, clean digital home that keeps the paper free for everyone while giving readers the option to support local journalism directly.
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          <div className="rounded-lg border border-[var(--paper-border)] bg-white p-6">
            <Award className="mb-3 text-[var(--gold)]" size={26} />
            <h3 className="font-display text-xl font-bold text-[var(--navy)]">Award-winning coverage</h3>
            <p className="font-serif-body mt-2 text-slate-600">Recognized by the Florida Press Association for community journalism, photography and editorial writing.</p>
          </div>
          <div className="rounded-lg border border-[var(--paper-border)] bg-white p-6">
            <Users className="mb-3 text-[var(--gold)]" size={26} />
            <h3 className="font-display text-xl font-bold text-[var(--navy)]">By the community, for the community</h3>
            <p className="font-serif-body mt-2 text-slate-600">Locally owned and operated, with a newsroom that lives, works and raises families right here on the island.</p>
          </div>
        </div>

        <h2 className="section-rule font-display mt-14 pt-3 text-2xl font-bold text-[var(--navy)]">Meet the newsroom</h2>
        <div className="mt-6 grid grid-cols-2 gap-6 sm:grid-cols-4">
          {STAFF.map((s) => (
            <div key={s.name} className="text-center">
              <img src={s.avatar} alt={s.name} className="mx-auto h-24 w-24 rounded-full object-cover ring-2 ring-[var(--gold)]/40" />
              <p className="font-display mt-3 text-lg font-bold text-[var(--ink)]">{s.name}</p>
              <p className="font-ui text-xs uppercase tracking-wider text-slate-500">{s.role}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-xl bg-[var(--navy)] p-8 text-center text-white">
          <h3 className="font-display text-2xl font-bold">Support local journalism</h3>
          <p className="font-serif-body mx-auto mt-2 max-w-lg text-white/80">Your subscription keeps our reporters on the beat and our community informed.</p>
          <Link to="/subscribe" className="font-ui mt-5 inline-block rounded-full bg-[var(--gold)] px-6 py-3 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#b08f48]">
            Become a Subscriber
          </Link>
        </div>
      </div>
    </div>
  );
}
